import { useMemo, useState } from 'react';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeader from '@/components/feature/PageHeader';
import ContentCard from '@/components/feature/ContentCard';
import Pagination from '@/components/base/Pagination';
import useSEO from '@/hooks/useSEO';
import { getNewsByDate, paginate, totalPages } from '@/lib/content';

const PAGE_SIZE = 6;

export default function NewsPage() {
  useSEO({
    title: '影视资讯与剧评 - 我的朋友他的妻子',
    description:
      '《我的朋友他的妻子》影视资讯汇总：收官剧评、拍摄幕后、主创专访与制作揭秘，按发布时间由新到旧排列，可切换栏目筛选。',
    keywords: '我的朋友他的妻子资讯,影视资讯,剧评,幕后故事,专访',
  });

  const [filter, setFilter] = useState('全部');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const allNews = useMemo(() => getNewsByDate(), []);

  const filters = useMemo(() => {
    const tags = new Set<string>();
    allNews.forEach((item) => tags.add(item.tags[0]));
    return ['全部', ...Array.from(tags).slice(0, 6)];
  }, [allNews]);

  const filtered = useMemo(
    () => (filter === '全部' ? allNews : allNews.filter((item) => item.tags.includes(filter))),
    [allNews, filter],
  );

  const pages = totalPages(filtered.length, PAGE_SIZE);
  const visible = paginate(filtered, page, PAGE_SIZE);

  const changeFilter = (next: string) => {
    setFilter(next);
    setPage(1);
  };

  return (
    <SiteLayout>
      <PageHeader
        eyebrow="NEWSROOM"
        title="影视资讯"
        description="从收官评论到美术手稿，从演员专访到配乐特辑。这里按发布时间收录与《我的朋友他的妻子》相关的全部报道与讨论，方便你顺着时间往回读。"
        crumbs={[{ label: '首页', to: '/' }, { label: '影视资讯' }]}
        image={allNews[0]?.image}
      >
        <div className="flex flex-wrap gap-2">
          {filters.map((tag, index) => (
            <button
              key={tag}
              type="button"
              onClick={() => changeFilter(tag)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors cursor-pointer md:text-sm ${
                filter === tag
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-300/70 text-foreground-400 hover:border-primary-500/50 hover:text-primary-300'
              } ${
                index > 2 && !filtersOpen ? 'hidden md:inline-flex' : 'inline-flex'
              }`}
            >
              {tag}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300/70 px-4 py-2 text-xs text-foreground-400 transition-colors hover:border-primary-500/50 hover:text-primary-300 cursor-pointer md:hidden"
          >
            {filtersOpen ? '收起筛选' : '更多筛选'}
            <i className={filtersOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
          </button>
        </div>
      </PageHeader>

      <section className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-400">
            共 <span className="text-primary-400">{filtered.length}</span> 篇资讯 · 按发布时间由新到旧
          </p>
          <span className="font-label text-xs tracking-[0.25em] text-foreground-600">
            PAGE {page} / {pages}
          </span>
        </div>

        <div className="mt-5 flex flex-col gap-4">
          {visible.map((item, index) => (
            <ContentCard key={item.id} item={item} variant="wide" index={index} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100 py-16 text-center">
            <i className="ri-file-search-line text-3xl text-foreground-600"></i>
            <p className="mt-3 text-sm text-foreground-300">该栏目暂无资讯</p>
            <button
              type="button"
              onClick={() => changeFilter('全部')}
              className="mt-4 rounded-md border border-primary-500/40 px-4 py-2 text-xs text-primary-300 transition-colors hover:bg-primary-500/10 cursor-pointer"
            >
              查看全部资讯
            </button>
          </div>
        )}

        <div className="mt-10">
          <Pagination page={page} total={pages} onChange={setPage} />
        </div>
      </section>
    </SiteLayout>
  );
}